import React from "react";
import { Box, Drawer, Toolbar, IconButton } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import JumboVerticalNavbar from "@jumbo/components/JumboVerticalNavbar/JumboVerticalNavbar";
import Logo from "@portal/shared/Logo";
import { routeSections } from "./routes";
import JumboLayoutSidebarContext from "@jumbo/components/JumboLayout/JumboLayoutSidebar/JumboLayoutSidebarContext";
import { SIDEBAR_VIEWS, SIDEBAR_VARIANTS } from "@jumbo/utils/constants/layout";
import { config as portalConfig } from "@portal/config/main";
import PropTypes from "prop-types";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

export const drawerWidth = 250;
export const miniWidth = 80;

const NavMenu = ({ sidebarOptions, setSidebarOptions }) => {
  const sidebarTheme = React.useMemo(() => createTheme(portalConfig.theme.sidebar), []);

  const navPalette = React.useMemo(() => {
    const fallback = {
      action: { active: "#7352C7", hover: "#7352C7" },
      background: { active: "rgba(115,82,199,0.15)", hover: "#E9ECEF" },
      tick: { active: "#7352C7", hover: "#ADB5BD" },
    };
    const baseNav = sidebarTheme.palette?.nav || {};
    return {
      action: { ...fallback.action, ...(baseNav.action || {}) },
      background: { ...fallback.background, ...(baseNav.background || {}) },
      tick: { ...fallback.tick, ...(baseNav.tick || {}) },
    };
  }, [sidebarTheme]);

  const mergedTheme = React.useMemo(
    () =>
      createTheme({
        ...portalConfig.theme.sidebar,
        palette: {
          ...portalConfig.theme.sidebar.palette,
          nav: navPalette,
        },
      }),
    [navPalette]
  );

  const handleNavItemClick = React.useCallback(() => {
    if (sidebarOptions?.open && sidebarOptions?.variant === SIDEBAR_VARIANTS.TEMPORARY) {
      setSidebarOptions((prev) => ({ ...prev, open: false }));
    }
  }, [setSidebarOptions, sidebarOptions?.open, sidebarOptions?.variant]);

  const menuItems = React.useMemo(() => {
    const mapItem = (item) => {
      if (item.children?.length) {
        return {
          uri: item.path,
          label: item.label,
          icon: item.icon,
          type: "collapsible",
          children: item.children.map((child) => ({
            uri: child.path,
            label: child.label,
            icon: child.icon,
            type: "nav-item",
            onClick: { onClick: handleNavItemClick },
          })),
        };
      }
      return {
        uri: item.path,
        label: item.label,
        icon: item.icon,
        type: "nav-item",
        onClick: { onClick: handleNavItemClick },
      };
    };

    return routeSections.map((section) => ({
      label: section.title,
      type: "section",
      children: section.items.map(mapItem),
    }));
  }, [handleNavItemClick]);

  const isMiniView = sidebarOptions?.view === SIDEBAR_VIEWS.MINI;
  const width = sidebarOptions?.width || drawerWidth;
  const min = sidebarOptions?.minWidth || miniWidth;
  const isMiniClosed = isMiniView && sidebarOptions?.open === false;
  const currentWidth = isMiniClosed ? min : width;

  const handleClose = React.useCallback(() => {
    setSidebarOptions((prev) => ({ ...prev, open: false }));
  }, [setSidebarOptions]);
  const toggleMini = React.useCallback(() => {
    setSidebarOptions((prev) => ({ ...prev, open: false }));
  }, [setSidebarOptions]);

  return (
    <ThemeProvider theme={mergedTheme}>
      <JumboLayoutSidebarContext.Provider value={{ sidebarOptions, setSidebarOptions }}>
        <Drawer
          anchor={sidebarOptions?.anchor || "left"}
          open={sidebarOptions?.open}
          onClose={handleClose}
          variant={sidebarOptions?.view === SIDEBAR_VIEWS.MINI && sidebarOptions?.open ? SIDEBAR_VARIANTS.TEMPORARY : sidebarOptions?.variant || SIDEBAR_VARIANTS.PERSISTENT}
          transitionDuration={250}
          hideBackdrop={sidebarOptions?.view === SIDEBAR_VIEWS.MINI}
          ModalProps={{ keepMounted: true, disableEnforceFocus: true }}
          sx={{
            '& .MuiDrawer-paper': {
              border: 'none',
              boxShadow: 25,
              transition: (theme) => theme.transitions.create(['width']),
              width: currentWidth,
              bgcolor: mergedTheme.palette.background.paper,
            },
          }}
        >
        <Box
          component="aside"
          sx={{
            width: currentWidth,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            borderRight: 1,
            borderColor: mergedTheme.palette.divider,
            bgcolor: mergedTheme.palette.background.paper,
            height: "100vh",
          }}
        >
          <Toolbar
            sx={{
              minHeight: 80,
              px: 3,
              borderBottom: 1,
              borderColor: mergedTheme.palette.divider,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Logo mini={false} mode={mergedTheme.palette?.mode || "light"} logoSize={50} />
            <IconButton size="small" onClick={toggleMini} sx={{ color: mergedTheme.palette.text.primary }}>
              <MenuOpenIcon sx={{ transform: !isMiniView ? "scaleX(1)" : "none" }} />
            </IconButton>
          </Toolbar>
          <JumboScrollbar autoHide autoHideDuration={200} autoHideTimeout={500}>
            <Box sx={{ px: 2, py: 1 }}>
              <JumboVerticalNavbar translate items={menuItems} />
            </Box>
          </JumboScrollbar>
        </Box>
        </Drawer>
      </JumboLayoutSidebarContext.Provider>
    </ThemeProvider>
  );
};

NavMenu.propTypes = {
  sidebarOptions: PropTypes.shape({ view: PropTypes.string, open: PropTypes.bool }).isRequired,
  setSidebarOptions: PropTypes.func.isRequired,
};

export default NavMenu;
